import { useEffect, useState } from "react";
import { Check, Crown, Lightning, ArrowRight } from "@phosphor-icons/react";
import { fetchTiers, fetchTopups, FALLBACK_TIERS, FALLBACK_TOPUPS, PRO_PERKS } from "../lib/tiers";

const ACCENT = "#FFD60A";

/**
 * Shared premium pricing block — Monthly/Yearly toggle + tier cards + top-up
 * packs. Values come from the backend billing catalog (falls back to a static
 * copy for offline safety only).
 */
export default function TierPricing({ onChoose, onTopup, currentTier, currentBilling, showTopups = true, defaultBilling = "monthly" }) {
  const [billing, setBilling] = useState(defaultBilling);
  const [tiers, setTiers] = useState(FALLBACK_TIERS);
  const [packs, setPacks] = useState(FALLBACK_TOPUPS);
  const annual = billing === "annual";

  useEffect(() => {
    let alive = true;
    fetchTiers().then((t) => alive && setTiers(t));
    fetchTopups().then((p) => alive && setPacks(p));
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="tier-pricing">
      {/* Billing toggle */}
      <div className="flex flex-col items-center gap-3 mb-10">
        <div className="inline-flex items-center rounded-full bg-white/5 border border-white/10 p-1" data-testid="billing-toggle">
          <button data-testid="toggle-monthly" onClick={() => setBilling("monthly")} className={`px-6 py-2.5 rounded-full text-sm font-bold transition-colors ${!annual ? "text-black" : "text-zinc-300 hover:text-white"}`} style={!annual ? { backgroundColor: "#fff" } : {}}>Monthly</button>
          <button data-testid="toggle-annual" onClick={() => setBilling("annual")} className={`px-6 py-2.5 rounded-full text-sm font-bold transition-colors ${annual ? "text-black" : "text-zinc-300 hover:text-white"}`} style={annual ? { backgroundColor: "#fff" } : {}}>Yearly</button>
        </div>
        <span
          className={`text-xs font-black uppercase tracking-wide px-3 py-1 rounded-full border transition-all ${annual ? "opacity-100 scale-100" : "opacity-60 scale-95"}`}
          style={{ color: ACCENT, borderColor: `${ACCENT}66`, backgroundColor: `${ACCENT}14`, boxShadow: annual ? `0 0 18px ${ACCENT}55` : "none" }}
        >
          Save 17% (2 Months Free!)
        </span>
      </div>

      {/* Tier cards */}
      <div className="grid md:grid-cols-3 gap-6 items-stretch max-w-5xl mx-auto">
        {tiers.map((t) => {
          const o = annual ? t.annual : t.monthly;
          if (!o) return null;
          const planId = o.plan_id;
          const isCurrent = currentTier === t.key && currentBilling === billing;
          // Prefer catalog fields (per_month), fall back to (amount/12) so it
          // works with both new backend payloads and the FALLBACK shape.
          const perMonth = annual
            ? (o.per_month ?? Math.round((o.amount || 0) / 12))
            : o.amount;
          const annualTotal = annual ? o.amount : null;
          return (
            <div key={t.key} data-testid={`pricing-tier-${t.key}`} className={`relative rounded-3xl p-8 flex flex-col transition-transform hover:-translate-y-1 ${t.popular ? "bg-[#141414]" : "bg-[#0E0E0E] border border-white/10"}`} style={t.popular ? { border: `2px solid ${ACCENT}` } : {}}>
              {t.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-black uppercase tracking-wider px-3 py-1 rounded-full text-black inline-flex items-center gap-1" style={{ backgroundColor: ACCENT }}><Crown weight="fill" size={12} /> Most popular</span>}
              <h3 className="font-display font-bold text-xl tracking-tight">{t.name}</h3>
              <p className="text-sm text-zinc-400 mt-1 mb-5 min-h-[40px]">{t.blurb}</p>
              <div className="flex items-end gap-1 mb-1">
                <span className="font-display font-black text-5xl tracking-tighter">${perMonth}</span>
                <span className="text-zinc-500 mb-1.5">/mo</span>
              </div>
              <p className="text-xs text-zinc-500 mb-6 min-h-[16px]">{annual ? `$${annualTotal} billed once a year` : "billed monthly"}</p>
              <div className="inline-flex items-center gap-1.5 text-sm font-bold mb-6" style={{ color: ACCENT }}>
                <Lightning weight="fill" size={15} /> {annual ? `${o.credits} credits upfront` : `${o.credits} AI credits / month`}
              </div>
              <button
                data-testid={`choose-${t.key}`}
                disabled={isCurrent}
                onClick={() => onChoose && onChoose(planId)}
                className={`w-full py-3 rounded-full font-bold transition-transform hover:scale-[1.02] inline-flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:hover:scale-100 ${t.popular ? "text-black" : "border border-white/15 text-white hover:bg-white/5"}`}
                style={t.popular ? { backgroundColor: ACCENT } : {}}
              >
                {isCurrent ? "Current plan" : <>Get {t.name} <ArrowRight weight="bold" size={16} /></>}
              </button>
            </div>
          );
        })}
      </div>

      {/* Shared perks */}
      <div className="max-w-3xl mx-auto mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-2">
        {PRO_PERKS.map((p) => (
          <div key={p} className="flex items-center gap-2 text-sm text-zinc-300"><Check weight="bold" size={15} className="shrink-0" style={{ color: ACCENT }} /> {p}</div>
        ))}
      </div>

      {/* Top-up impulse packs */}
      {showTopups && (
        <div className="max-w-3xl mx-auto mt-16" data-testid="topup-packs">
          <div className="text-center mb-6">
            <h3 className="font-display font-black text-2xl tracking-tight">Need a quick boost?</h3>
            <p className="text-sm text-zinc-400">One-time credit packs — they never expire, no subscription needed.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {packs.map((pk) => {
              const id = pk.pack_id || pk.id;
              return (
                <div key={id} data-testid={`topup-${id}`} className="rounded-2xl border border-white/10 bg-[#0E0E0E] p-6 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-display font-bold text-lg">+{pk.credits} credits</p>
                    <p className="text-xs text-zinc-500">{pk.name || pk.label} · one-time</p>
                    <p className="text-[11px] text-zinc-400 mt-1">
                      <span className="line-through text-zinc-600">${pk.amount_full}</span>
                      <span className="ml-1.5 font-bold text-white">${pk.amount_member}</span>
                      <span className="ml-1 text-zinc-500">for members</span>
                    </p>
                  </div>
                  <button data-testid={`buy-topup-${id}`} onClick={() => onTopup && onTopup(id)} className="px-5 py-2.5 rounded-full font-bold text-black transition-transform hover:scale-105 whitespace-nowrap" style={{ backgroundColor: ACCENT }}>
                    Buy
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
